import React, { useRef, useState, useEffect } from "react";
import { AppId, FCX, ProductLogo, sanitize, usePlatformContext } from "@datassential/platform-ui-lib";
import './ReadAboutIt.scss';
import cn from "classnames";
import Widget from "@components/Widget/Widget";
import PublicationCard, { PublicationCardTooltip } from "@components/PublicationCard/PublicationCard";
import { useMenuPublications } from "@apiHooks/useMenuPublications";

const REPORT_PRO_TAG_ID = 14;
const CARD_WIDTH = 135;
const CARD_GAP = 16;
const CONTAINER_PADDING = 48;
const MIN_CARDS = 4;
const MAX_CARDS = 10;

const ReadAboutIt: FCX = ({
    className,
    testId,
    style,
}) => {
    const {
        data: reports,
        isLoading,
    } = useMenuPublications();

    const {
        userInfo: {
            products,
            reportProDetailsUrl,
        },
    } = usePlatformContext();
    const productRP = products[AppId.ReportPro];
    const detailsPath = process.env.REACT_APP_RP_ITEM_LINK ?? reportProDetailsUrl;

    const contentRef = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState(MIN_CARDS);

    useEffect(() => {
        if (!contentRef.current || isLoading) return;

        const calculateCount = () => {
            // Use clientWidth to get inner width (excludes scrollbar, includes padding)
            const containerWidth = contentRef.current!.clientWidth;
            
            // Check actual card size if cards are rendered
            const cards = contentRef.current!.querySelectorAll('.ReadAboutIt__card');
            if (cards.length > 0) {
                const computedStyle = window.getComputedStyle(contentRef.current!);
                const actualGap = parseFloat(computedStyle.gap) || CARD_GAP;
                const actualPadding = parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight);

                const availableWidth = containerWidth - actualPadding;
                const minCardWidth = 125; // Minimum card width from CSS
                
                // Calculate with actual values, accounting for flex-shrink
                let count = 0;
                for (let i = 1; i <= MAX_CARDS; i++) {
                    // Calculate required width with gaps
                    const gapsWidth = (i - 1) * actualGap;
                    const remainingForCards = availableWidth - gapsWidth;
                    const cardWidthNeeded = remainingForCards / i;

                    // Check if cards can shrink to fit (must be >= minCardWidth)
                    if (cardWidthNeeded >= minCardWidth) {
                        count = i;
                    } else {
                        break;
                    }
                }
                setVisibleCount(Math.max(count, 1));
            } else {
                // Fallback when no cards are rendered yet
                const availableWidth = containerWidth - CONTAINER_PADDING;
                let count = 0;
                for (let i = 1; i <= MAX_CARDS; i++) {
                    const totalWidth = (i * CARD_WIDTH) + ((i - 1) * CARD_GAP);
                    if (totalWidth <= availableWidth) {
                        count = i;
                    } else {
                        break;
                    }
                }
                setVisibleCount(Math.max(count, Math.min(MIN_CARDS, MAX_CARDS)));
            }
        };

        calculateCount();
        window.addEventListener('resize', calculateCount);
        return () => window.removeEventListener('resize', calculateCount);
    }, [isLoading]);

    return (
        <Widget
            className={cn(
                "ReadAboutIt",
                className,
                isLoading && "ReadAboutIt--loading"
            )}
            style={style}
            headerContent={(
                <div className="ReadAboutIt__header">
                    <div className="ReadAboutIt__header-title">
                        READ ABOUT IT:
                    </div>
                    {!isLoading && (
                        <a
                            className="ReadAboutIt__view-more appears"
                            target="_blank"
                            rel="noreferrer"
                            href={sanitize(`${productRP.url}/?tags=${REPORT_PRO_TAG_ID}`)}
                            data-testid={`buttonMacReportsItemViewMore`}
                        >View all reports</a>
                    )}
                </div>
            )}
            testId={testId}
        >
            <div 
                ref={contentRef}
                className={cn("ReadAboutIt__content", isLoading && "ReadAboutIt__content--loading")}
            >
                {isLoading && (
                    <div className="TrendingNow__spinner">
                        <ProductLogo appId={AppId.Snap} type="regular"/>
                    </div>
                )}
                {!isLoading && reports.slice(0, visibleCount).map((report, index) => (
                    <React.Fragment key={report.id}>
                        <PublicationCard
                            className="ReadAboutIt__card"
                            data={report}
                            index={index}
                            link={`${productRP.url}${detailsPath}${report.id}`}
                            tooltipId={`macs-reports-item-${report.id}`}
                            testIdBase="MacsReportsItem"
                        />
                        <PublicationCardTooltip
                            className={"ReadAboutIt__card-tooltip"}
                            data={report}
                            index={index}
                            tooltipId={`macs-reports-item-${report.id}`}
                            testIdBase="MacsReportsItem"
                        />
                    </React.Fragment>
                ))}
            </div>
        </Widget>
    );
};

export default ReadAboutIt;
